import { useState, useEffect, useCallback } from 'react';
import { getProjectContracts, addContractToProject, removeContractFromProject, updateProjectContractProducts, getContracts, getContractProducts, getProducts, getBusinessUnits } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Plus, X, FileText, Clock, Loader2, AlertCircle, Package, Building2, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import { it } from 'date-fns/locale';

export default function ProjectContracts({ projectId, clientId, canEdit = false }) {
  const [contracts, setContracts] = useState([]);
  const [allContracts, setAllContracts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allBUs, setAllBUs] = useState([]);
  const [contractProductsCache, setContractProductsCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProductFor, setSavingProductFor] = useState(null); // contract_id being auto-saved
  const [showHistory, setShowHistory] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [dialogProducts, setDialogProducts] = useState([]);
  const [dialogSelectedIds, setDialogSelectedIds] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (projectId) loadData();
  }, [projectId, showHistory]);

  const loadData = async () => {
    try {
      const [pcRes, contractsRes, productsRes, busRes] = await Promise.all([
        getProjectContracts(projectId, showHistory),
        getContracts({ client_id: clientId }),
        getProducts().catch(() => ({ data: [] })),
        getBusinessUnits().catch(() => ({ data: [] })),
      ]);
      setContracts(pcRes.data);
      setAllContracts(contractsRes.data);
      setAllProducts(productsRes.data);
      setAllBUs(busRes.data);

      // Pre-fetch contract products for active contracts
      const activePCs = pcRes.data.filter(c => !c.end_date);
      const cache = {};
      await Promise.all(activePCs.map(async (pc) => {
        try {
          const res = await getContractProducts(pc.contract_id);
          cache[pc.contract_id] = res.data || [];
        } catch { cache[pc.contract_id] = []; }
      }));
      setContractProductsCache(cache);
    } catch {
      toast({ title: 'Errore nel caricamento contratti', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Dialog: load products when selecting a contract
  useEffect(() => {
    if (!selectedContractId) { setDialogProducts([]); setDialogSelectedIds([]); return; }
    (async () => {
      setLoadingProducts(true);
      try {
        const res = await getContractProducts(selectedContractId);
        const cps = res.data || [];
        setDialogProducts(cps);
        // Auto-select: if all belong to 1 BU, select all
        const pids = cps.map(cp => cp.product_id);
        const buSet = new Set();
        pids.forEach(pid => {
          const prod = allProducts.find(p => p.id === pid);
          if (prod?.business_unit_id) buSet.add(prod.business_unit_id);
        });
        setDialogSelectedIds(buSet.size <= 1 ? pids : []);
      } catch { setDialogProducts([]); }
      finally { setLoadingProducts(false); }
    })();
  }, [selectedContractId]);

  const handleAddContract = async () => {
    if (!selectedContractId || (dialogProducts.length > 0 && dialogSelectedIds.length === 0)) return;
    setSaving(true);
    try {
      await addContractToProject(projectId, selectedContractId, dialogSelectedIds);
      toast({ title: 'Contratto associato' });
      setDialogOpen(false);
      setSelectedContractId('');
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      // If the conflict is about a product overlap with another contract on the same project,
      // ask the admin if it's a renewal and retry with force=true.
      const isOverlapError = /già attivo in un altro contratto|già assegnat/i.test(detail);
      if (isOverlapError && window.confirm(
        `${detail}\n\nÈ un contratto di rinnovo? Confermando, l'associazione verrà comunque effettuata.`
      )) {
        try {
          await addContractToProject(projectId, selectedContractId, dialogSelectedIds, true);
          toast({ title: 'Contratto associato come rinnovo' });
          setDialogOpen(false);
          setSelectedContractId('');
          loadData();
        } catch (err2) {
          toast({ title: 'Errore', description: err2.response?.data?.detail || 'Impossibile associare', variant: 'destructive' });
        }
      } else {
        toast({ title: 'Errore', description: detail || 'Impossibile associare', variant: 'destructive' });
      }
    } finally { setSaving(false); }
  };

  const handleRemoveContract = async (contractId) => {
    if (!confirm('Rimuovere questo contratto dal progetto?')) return;
    try {
      await removeContractFromProject(projectId, contractId);
      toast({ title: 'Contratto rimosso' });
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: error.response?.data?.detail || 'Impossibile rimuovere', variant: 'destructive' });
    }
  };

  // Auto-save: toggle product directly on an existing contract
  const handleToggleProduct = useCallback(async (contractId, productId, currentSelected) => {
    const newIds = currentSelected.includes(productId)
      ? currentSelected.filter(id => id !== productId)
      : [...currentSelected, productId];
    if (newIds.length === 0) {
      toast({ title: 'Almeno un prodotto richiesto', variant: 'destructive' });
      return;
    }
    setSavingProductFor(contractId);
    try {
      await updateProjectContractProducts(projectId, contractId, newIds);
      // Update local state immediately
      setContracts(prev => prev.map(pc =>
        pc.contract_id === contractId ? { ...pc, selected_product_ids: newIds } : pc
      ));
    } catch (error) {
      toast({ title: 'Errore', description: error.response?.data?.detail || 'Impossibile aggiornare', variant: 'destructive' });
    } finally { setSavingProductFor(null); }
  }, [projectId]);

  const getProductName = (pid) => allProducts.find(p => p.id === pid)?.name || 'Prodotto';
  const getBUName = (buId) => allBUs.find(b => b.id === buId)?.name || '';
  const getProductBU = (pid) => {
    const prod = allProducts.find(p => p.id === pid);
    return prod?.business_unit_id ? getBUName(prod.business_unit_id) : '';
  };
  const getContractName = (cid) => allContracts.find(c => c.id === cid)?.name || 'Contratto';
  const getContractDates = (cid) => {
    const c = allContracts.find(ct => ct.id === cid);
    if (!c) return '';
    const s = c.start_date ? format(parseISO(c.start_date), 'dd/MM/yy', { locale: it }) : '?';
    const e = c.end_date ? format(parseISO(c.end_date), 'dd/MM/yy', { locale: it }) : 'in corso';
    return `${s} → ${e}`;
  };
  const isContractActive = (pc) => {
    if (pc.end_date) return false;
    const c = allContracts.find(ct => ct.id === pc.contract_id);
    if (!c) return false;
    const now = new Date();
    if (isBefore(now, new Date(c.start_date))) return false;
    if (c.end_date && isAfter(now, new Date(c.end_date))) return false;
    return true;
  };

  const associatedIds = contracts.filter(c => !c.end_date).map(c => c.contract_id);
  const availableContracts = allContracts.filter(c => !associatedIds.includes(c.id));
  const activeContracts = contracts.filter(c => !c.end_date);
  const historicalContracts = contracts.filter(c => c.end_date);

  if (loading) return <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-orange-600" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-orange-600" />
          <h3 className="font-medium text-sm">Contratti e Prodotti</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {historicalContracts.length > 0 && (
            <Button variant="ghost" size="sm" type="button" onClick={() => setShowHistory(!showHistory)} className="h-7 text-xs">
              <Clock className="w-3 h-3 mr-1" />{showHistory ? 'Nascondi storico' : 'Storico'}
            </Button>
          )}
          {canEdit && (
            <Button size="sm" type="button" onClick={() => setDialogOpen(true)} className="h-7 text-xs" data-testid="add-contract-btn">
              <Plus className="w-3 h-3 mr-1" />Aggiungi Contratto
            </Button>
          )}
        </div>
      </div>

      {activeContracts.length === 0 ? (
        <div className="text-center py-3 text-gray-400 text-sm flex items-center justify-center gap-2 bg-gray-50 rounded-md">
          <AlertCircle className="w-4 h-4" />Nessun contratto associato
        </div>
      ) : (
        <div className="space-y-3">
          {activeContracts.map((pc) => {
            const selIds = pc.selected_product_ids || [];
            const needsProducts = selIds.length === 0;
            const cProducts = contractProductsCache[pc.contract_id] || [];
            const isSaving = savingProductFor === pc.contract_id;

            return (
              <div key={pc.id} className={`rounded-lg border ${needsProducts ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
                {/* Contract header */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-3.5 h-3.5 text-orange-600 shrink-0" />
                    <span className="font-medium text-sm truncate">{getContractName(pc.contract_id)}</span>
                    <span className="text-xs text-gray-400 shrink-0">{getContractDates(pc.contract_id)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {needsProducts && (
                      <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-100 text-xs h-5" data-testid="needs-products-badge">
                        <AlertCircle className="w-3 h-3 mr-0.5" />Assegna prodotti
                      </Badge>
                    )}
                    {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />}
                    {canEdit && (
                      <Button variant="ghost" size="sm" type="button" className="h-6 w-6 p-0" onClick={() => handleRemoveContract(pc.contract_id)}>
                        <X className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Products - always visible */}
                <div className="px-3 py-2">
                  {cProducts.length === 0 ? (
                    <p className="text-xs text-gray-400">Nessun prodotto nel contratto</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {cProducts.map(cp => {
                        const pid = cp.product_id;
                        const checked = selIds.includes(pid);
                        const buName = getProductBU(pid);
                        return (
                          <label
                            key={pid}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-pointer transition-all border ${
                              checked
                                ? 'bg-orange-50 border-orange-300 text-orange-800 font-medium'
                                : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                            } ${!canEdit ? 'pointer-events-none' : ''} ${isSaving ? 'opacity-60 pointer-events-none' : ''}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => handleToggleProduct(pc.contract_id, pid, selIds)}
                              disabled={!canEdit || isSaving}
                              className="w-3.5 h-3.5"
                            />
                            <Package className="w-3 h-3" />
                            {getProductName(pid)}
                            {buName && (
                              <span className="text-[10px] opacity-70 ml-0.5">({buName})</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Historical */}
      {showHistory && historicalContracts.length > 0 && (
        <div className="pt-2 border-t">
          <h4 className="text-xs font-medium text-gray-400 mb-1.5">Storico</h4>
          {historicalContracts.map((pc) => (
            <div key={pc.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded opacity-50 text-xs mb-1">
              <FileText className="w-3 h-3 text-gray-400" />
              <span>{getContractName(pc.contract_id)}</span>
              <Badge variant="outline" className="text-[10px] h-4">Terminato</Badge>
            </div>
          ))}
        </div>
      )}

      {/* Add Contract Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setSelectedContractId(''); setDialogProducts([]); setDialogSelectedIds([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Associa Contratto</DialogTitle>
            <DialogDescription>Seleziona un contratto e i prodotti da includere nel progetto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Contratto</Label>
              <Select value={selectedContractId} onValueChange={setSelectedContractId}>
                <SelectTrigger data-testid="select-contract">
                  <SelectValue placeholder="Seleziona contratto" />
                </SelectTrigger>
                <SelectContent>
                  {availableContracts.length === 0 ? (
                    <SelectItem value="none" disabled>Nessun contratto disponibile</SelectItem>
                  ) : (
                    availableContracts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} {c.start_date ? `(${format(parseISO(c.start_date), 'dd/MM/yy', { locale: it })})` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedContractId && (
              <div className="space-y-2">
                <Label>Prodotti</Label>
                {loadingProducts ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento...</div>
                ) : dialogProducts.length === 0 ? (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />Contratto senza prodotti. Aggiungi prima i prodotti al contratto.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {dialogProducts.map(cp => {
                      const pid = cp.product_id;
                      const checked = dialogSelectedIds.includes(pid);
                      const buName = getProductBU(pid);
                      return (
                        <label key={pid} className={`flex items-center gap-2.5 p-2 rounded-md cursor-pointer transition-colors border ${checked ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-transparent hover:bg-gray-100'}`}>
                          <Checkbox checked={checked} onCheckedChange={() => setDialogSelectedIds(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid])} />
                          <Package className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span className="text-sm font-medium text-gray-700">{getProductName(pid)}</span>
                          {buName && <Badge variant="outline" className="text-xs ml-auto"><Building2 className="w-3 h-3 mr-1" />{buName}</Badge>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleAddContract} disabled={saving || !selectedContractId || (dialogProducts.length > 0 && dialogSelectedIds.length === 0)} data-testid="confirm-add-contract-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Associa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
